import React, {useEffect, useState} from "react";
import ProgressBar from '../../progress-bar.js';
import * as yup from "yup";
import {
    EmailValidation,
    FirstNameValidation,
    LastNameValidation,
    MobileValidation,
    RecaptchaValidation,
    TermsAndConditionsValidation
} from "../components/FormValidations";
import {SuccessMessage} from "../components/Messages";
import {
    FAQLink,
    FormSubmissionError,
    RequiredAstrix,
    RequiredFieldMessage,
    TermsConditionsLink
} from "../components/FormElements";
import {Formik} from "formik";
import InputField from "../components/InputField";
import CheckboxField from "../components/CheckboxField";
import handleFormSubmit, {futureCaptainEndpoint} from "../client-api/ANZAPI";
import {removePageClassContent} from "../../script";
import NumberField from "../components/NumberField";
import RecapthaField from "../components/RecapthaField";
import SelectField from "../components/SelectField";
import _ from 'lodash';
import {updateLinkTrackingFields} from "../components/LinkTracking";
import DateSelectorField from "../components/DateSelectorField";

const FIRST_NAME_FIELD = 'FirstName';
const LAST_NAME_FIELD = 'LastName';
const EMAIL_FIELD = 'Email';
const MOBILE_FIELD = 'Mobile';
const OTHER_NUMBER_FIELD = 'OtherNumber';
const GAME_REGION_FIELD = 'GameRegion';
const CHILD_RELATIONSHIP_FIELD = 'ChildRelationship';
const CHILD_CONSENT = 'ChildConsent';
const CHILD_FIRST_NAME_FIELD = 'ChildFirstName';
const CHILD_LAST_NAME_FIELD = 'ChildLastName';
const CHILD_DOB_FIELD = 'ChildDOB';
const CHILD_SCHOOL_NAME_FIELD = 'ChildSchoolName';
const CHILD_SUBURB = 'ChildSuburb';
const AGE_CHECK_FIELD = 'AgeCheck';
const AGREE_TERMS_FIELD = 'AgreeTC';
const SIGNUP_FIELD = 'SignUp';
const RECAPTCHA_FIELD = 'Recaptcha';

const initialValues = {
    [FIRST_NAME_FIELD]: '',
    [LAST_NAME_FIELD]: '',
    [EMAIL_FIELD]: '',
    [MOBILE_FIELD]: '',
    [OTHER_NUMBER_FIELD]: '',
    [GAME_REGION_FIELD]: '',
    [CHILD_RELATIONSHIP_FIELD]: '',
    [CHILD_CONSENT]: false,
    [CHILD_FIRST_NAME_FIELD]: '',
    [CHILD_LAST_NAME_FIELD]: '',
    [CHILD_DOB_FIELD]: '',
    [CHILD_SCHOOL_NAME_FIELD]: '',
    [CHILD_SUBURB]: '',
    [AGE_CHECK_FIELD]: false,
    [AGREE_TERMS_FIELD]: false,
    [SIGNUP_FIELD]: false,
    [RECAPTCHA_FIELD]: ''
}

const regionOptions = [
    {label: 'Auckland (Central)'},
    {label: 'Auckland (North)'},
    {label: 'Auckland (West)'},
    {label: 'Auckland (South)'},
    {label: 'Hamilton'},
    {label: 'Tauranga'},
    {label: 'Rotorua'},
    {label: 'Palmerston North'},
    {label: 'Porirua'},
    {label: 'Wellington'},
    {label: 'Nelson'},
    {label: 'Christchurch'},
    {label: 'Rangiora'},
    {label: 'Dunedin'},
    {label: 'Invercargill'},
];

const childRelationOptions = [
    {label: 'Parent/Guardian'},
    {label: 'Relative'},
    {label: 'Family Friend'},
]

const futureCaptainSubmissionTracking = {
    pageName: 'anzcourtside:futurecaptain:form:confirmation',
    formName: 'anzcourtside:futurecaptain',
    formStatus: 'submitted',
    journeyStepName: 'confirmation',
    journeyName: 'competition:netball_future_captain',
    journeyStatus: 'complete'
}


const submissionValidation = yup.object().shape({
    [FIRST_NAME_FIELD]: FirstNameValidation,
    [LAST_NAME_FIELD]: LastNameValidation,
    [CHILD_DOB_FIELD]: yup.date(),
    [CHILD_SCHOOL_NAME_FIELD]: yup.string().required('Child school name cannot be left blank'),
    [CHILD_SUBURB]: yup.string().required('Child suburb cannot be left blank'),
    [EMAIL_FIELD]: EmailValidation,
    [MOBILE_FIELD]: MobileValidation,
    [GAME_REGION_FIELD]: yup.string().required('Choose the city of the ANZ Premiership game(s) you would like to nominate a child for'),
    [CHILD_RELATIONSHIP_FIELD]: yup.string().required('Your relationship to child is required.'),
    [AGE_CHECK_FIELD]: yup.boolean()
        .required()
        .oneOf([true], "This child is between 8 and 12 years of age as at 22 April 2025"),
    [CHILD_CONSENT]: yup.boolean()
        .notRequired()
        .when(CHILD_RELATIONSHIP_FIELD, {
            is: (val) => [childRelationOptions[1]?.label, childRelationOptions[2]?.label].includes(val),
            then: yup.boolean().required().oneOf([true], "Child consent is a required field")
        }),
    [AGREE_TERMS_FIELD]: TermsAndConditionsValidation,
    [RECAPTCHA_FIELD]: RecaptchaValidation,
});

const FutureCaptainForm = (props) => {
    const [successSubmitted, setSuccessSubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);

    useEffect(() => {
        ProgressBar()
    }, []);

    return (
        <>
            {successSubmitted === true ? (
                <SuccessMessage
                    heading={'Thank you for your ANZ Future Captains entry'}
                    message={'Your entry has been received – good luck!'}
                />
            ) : (
                <>
                    <RequiredFieldMessage/>
                    <hr/>
                    <Formik
                        initialValues={{...initialValues}}
                        initialErrors={{...initialValues}}
                        onSubmit={(values, {setSubmitting, resetForm}) => {
                            handleFormSubmit(values, futureCaptainEndpoint(props.elementId))
                                .then((res) => {
                                    setSubmitting(false)
                                    if (res.success) {
                                        ProgressBar(2)
                                        resetForm({...initialValues});
                                        setSuccessSubmitted(true);
                                        removePageClassContent('.future-captain-content');
                                        updateLinkTrackingFields(futureCaptainSubmissionTracking);
                                        pageView();

                                        return;
                                    }

                                    setSubmissionError(res?.message)
                                    $('html, body').animate({
                                        'scrollTop': document.getElementById("submissionErrorId").offsetTop - 50
                                    }, 500);
                                })
                        }}
                        validationSchema={submissionValidation}
                    >{(formikProps) => (
                        <>
                            {submissionError !== null && (
                                <div id={'submissionErrorId'}>
                                    <FormSubmissionError message={submissionError}/>
                                </div>
                            )}

                            <form onSubmit={formikProps.handleSubmit}>
                                <InputField
                                    id={'firstName'}
                                    label={'First name'}
                                    name={FIRST_NAME_FIELD}
                                    required={true}
                                    maxLength={30}
                                    {...formikProps}
                                />

                                <InputField
                                    id={'lastName'}
                                    label={'Last name'}
                                    name={LAST_NAME_FIELD}
                                    required={true}
                                    maxLength={30}
                                    {...formikProps}
                                />

                                <InputField
                                    id={'email'}
                                    label={'Email'}
                                    type={'email'}
                                    name={EMAIL_FIELD}
                                    required={true}
                                    maxLength={129}
                                    {...formikProps}
                                />

                                <NumberField
                                    name={MOBILE_FIELD}
                                    label={'Mobile number'}
                                    id={'mobile'}
                                    allowNegative={false}
                                    className={'short'}
                                    maxLength={20}
                                    required={true}
                                />

                                <NumberField
                                    name={OTHER_NUMBER_FIELD}
                                    label={'Other phone number'}
                                    id={'otherNumber'}
                                    allowNegative={false}
                                    className={'short'}
                                    maxLength={20}
                                />

                                <hr/>
                                <h3>Competition details</h3>
                                <SelectField
                                    id={'region'}
                                    label={'Select your closest ANZ Premiership game region'}
                                    name={GAME_REGION_FIELD}
                                    options={regionOptions}
                                    selectClass={"short"}
                                    required={true}
                                    {...formikProps}
                                />

                                <SelectField
                                    id={'childRelationship'}
                                    label={'Your relationship to child'}
                                    name={CHILD_RELATIONSHIP_FIELD}
                                    options={childRelationOptions}
                                    selectClass={"short"}
                                    required={true}
                                    {...formikProps}
                                    handleChange={(e) => {
                                        formikProps.setFieldValue(CHILD_CONSENT, false)
                                        formikProps.handleChange(e)
                                    }}
                                />
                                {[childRelationOptions[1]?.label, childRelationOptions[2]?.label].includes(formikProps.values[CHILD_RELATIONSHIP_FIELD]) && (
                                    <div className={'form-group mb-0'}>
                                        <CheckboxField
                                            name={CHILD_CONSENT}
                                            value={formikProps.values[CHILD_CONSENT]}
                                            label={'I have consent from child\'s parent or legal guardian'}
                                            required={true}
                                        />
                                    </div>
                                )}

                                <hr/>
                                <h3>Child details</h3>

                                <InputField
                                    id={'childFirstName'}
                                    label={'First name'}
                                    name={CHILD_FIRST_NAME_FIELD}
                                    maxLength={30}
                                    {...formikProps}
                                />

                                <InputField
                                    id={'childLastName'}
                                    label={'Last name'}
                                    name={CHILD_LAST_NAME_FIELD}
                                    maxLength={40}
                                    {...formikProps}
                                />

                                <div className={'form-group'}>
                                    <label className={'heading d-block'}>Date of birth <RequiredAstrix/></label>
                                    <DateSelectorField name={CHILD_DOB_FIELD}/>
                                </div>

                                <InputField
                                    id={'childSchoolName'}
                                    label={'School name'}
                                    name={CHILD_SCHOOL_NAME_FIELD}
                                    required={true}
                                    maxLength={125}
                                    {...formikProps}
                                />

                                <InputField
                                    id={'childSuburb'}
                                    label={'Suburb'}
                                    name={CHILD_SUBURB}
                                    required={true}
                                    classNameWrapper={'pb-5'}
                                    maxLength={125}
                                    {...formikProps}
                                />

                                <div className={'form-group mt-0'}>
                                    <CheckboxField
                                        name={AGE_CHECK_FIELD}
                                        value={formikProps.values[AGE_CHECK_FIELD]}
                                        label={'This child is between 8 and 12 years of age as at 22 April 2025'}
                                        required={true}
                                    />
                                </div>

                                <div className={'form-group mt-0'}>
                                    <CheckboxField
                                        name={AGREE_TERMS_FIELD}
                                        value={formikProps.values[AGREE_TERMS_FIELD]}
                                        label={<>I agree to the <a href={"#"} data-toggle="modal"
                                                                   data-target="#TCFutureCaptain"> ANZ Future Captains
                                            Terms and Conditions</a></>}
                                        required={true}
                                    />
                                </div>

                                <div className={'form-group mt-0'}>
                                    <CheckboxField
                                        name={SIGNUP_FIELD}
                                        value={formikProps.values[SIGNUP_FIELD]}
                                        label={'Tick here to sign up to receive news, updates and exclusive offers from ANZ Courtside'}
                                    />
                                </div>

                                <div className={'pt-4'}>
                                    <RecapthaField name={RECAPTCHA_FIELD}/>
                                </div>

                                <hr/>

                                <div className={'links pt-0'}>
                                    <button
                                        type="submit"
                                        className={'btn btn-primary mr-5'}
                                        disabled={formikProps.isSubmitting || !_.isEmpty(formikProps.errors)}
                                    >Enter now
                                    </button>
                                    <TermsConditionsLink target={'TCFutureCaptain'}/>
                                </div>
                            </form>
                        </>
                    )}
                    </Formik>
                </>
            )}
        </>
    )
}

export default FutureCaptainForm;
